import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapPin, DollarSign, Search, Download, Upload, Users, List, Sparkles, Trash2, AlertCircle } from 'lucide-react';
import usePOCs from '@/hooks/pocs/usePOCs';
import usePOCBudgets from '@/hooks/pocs/usePOCBudgets';
import usePOCFileUpload from '@/hooks/pocs/usePOCExcelUpload';
import usePersistentState from '@/hooks/common/usePersistentState';
import POCTable from './POCTable';
import SelectedPOCsList from './SelectedPOCsList';
import { searchPocsColumns } from '../../hooks/table/useSearchPOCsColumns';
import { selectedPocsManualColumns } from '../../hooks/table/useSelectedPOCsColumns';
import { selectedPocsOptimizedColumns } from '../../hooks/table/selectedPocsOptimizedColumns';
import { useCampaignAPI } from '@/hooks/campaigns/useCampaignAPI';
import useCampaignPersistence from '@/hooks/campaigns/useCampaignPersistence';

// Budget Allocation Modes
const BudgetAllocationMode = {
  MANUAL: 'manual',
  OPTIMIZED: 'optimized'
};

// Budget Allocation Mode Enum (matches backend)
const BudgetAllocationModeEnum = {
  BPP: 'BPP',  // Budget Per POC
  BO: 'BO'     // Budget Optimized
};

// Labels for display


const POCSelectionForm = forwardRef(({
  campaignId,
  selectedPocs,
  onPocsChange,
  totalBudget,
  region,
  brand
}, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkBudget, setBulkBudget] = useState('');
  const [searchMode, setSearchMode] = useState('region');
  const [activeTab, setActiveTab] = useState('search'); // 'search' or 'selected'

  // Use persistent state for budget allocation settings
  const storageKey = `pocBudgetSettings_${campaignId || region}`;
  const {
    state: budgetSettings,
    updateState: updateBudgetSettings
  } = usePersistentState(storageKey, {
    globalBudgetInput: '',
    allocationMode: BudgetAllocationMode.MANUAL
  });

  // Extract values from persistent state
  const globalBudgetInput = budgetSettings.globalBudgetInput || '';
  const allocationMode = budgetSettings.allocationMode || BudgetAllocationMode.MANUAL;

  // Update helpers
  const setGlobalBudgetInput = useCallback((value) => {
    updateBudgetSettings({ globalBudgetInput: value });
  }, [updateBudgetSettings]);

  const setAllocationMode = useCallback((mode) => {
    updateBudgetSettings({ allocationMode: mode });
  }, [updateBudgetSettings]);

  // console.log("POCSelectionForm - selectedPocs:", selectedPocs);
  // console.log("POCSelectionForm - campaignId:", campaignId);
  // console.log("POCSelectionForm - Persisted budget settings:", budgetSettings);

  // Use POC budgets hook
  const { pocBudgets, setPocBudgets } = usePOCBudgets(selectedPocs);

  // Use POC file upload hook
  const { 
    loading: uploadLoading, 
    error: uploadError, 
    uploadStats, 
    processPOCFiles, 
    clearError: clearUploadError, 
    clearStats 
  } = usePOCFileUpload(campaignId);

  // Use the POCs hook
  const {
    loading: pocsLoading,
    error: pocsError,
    pocs,
    totalCount,
    pagination,
    fetchPOCsByRegion,
    searchPOCs,
    loadMorePOCs,
    clearPOCs,
    hasMorePOCs
  } = usePOCs(region);

  // Calculate remaining budget
  const allocatedBudget = Object.values(pocBudgets).reduce((sum, budget) => sum + (parseInt(budget) || 0), 0);
  const remainingBudget = totalBudget - allocatedBudget;

  // DISABLED: Auto-loading POCs from backend
  // This was preventing users from managing their POC list properly
  // Users should manually select POCs instead of auto-loading from backend
  
  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        setSearchMode('query');
        searchPOCs(searchTerm.trim());
      } else if (searchTerm.trim().length === 0 && region) {
        setSearchMode('region');
        fetchPOCsByRegion(region);
      } else {
        clearPOCs();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, region, searchPOCs, fetchPOCsByRegion, clearPOCs]);

  // Initial load
  useEffect(() => {
    if (region && searchTerm.trim().length === 0) {
      fetchPOCsByRegion(region);
    }
  }, [region, fetchPOCsByRegion]);

  // Handle POC selection
  const handlePocSelect = (poc) => {
    const safeSelectedPocs = selectedPocs || [];
    const isSelected = safeSelectedPocs.some(p => p.poc_id === poc.poc_id);
    
    if (isSelected) {
      const newSelected = safeSelectedPocs.filter(p => p.poc_id !== poc.poc_id);
      onPocsChange(newSelected);
      
      const newBudgets = { ...pocBudgets };
      delete newBudgets[poc.poc_id];
      setPocBudgets(newBudgets);
    } else {
      const budgetValue = bulkBudget && !isNaN(bulkBudget) ? parseInt(bulkBudget) : null;
      
      const newPoc = {
        poc_id: poc.poc_id,
        campaign_id: campaignId,
        poc_budget: budgetValue,
        poc_name: poc.poc_name,
        address: poc.address,
        city: poc.city,
        region: poc.region,
        country: poc.country,
        latitude: poc.latitude,
        longitude: poc.longitude
      };
      onPocsChange([...selectedPocs, newPoc]);
      
      if (budgetValue !== null) {
        setPocBudgets(prev => ({ ...prev, [poc.poc_id]: budgetValue }));
      }
    }
  };

  const handleRemovePOC = async (pocId) => {
    const newSelected = (selectedPocs || []).filter(p => p.poc_id !== pocId);
    onPocsChange(newSelected);
    
    const newBudgets = { ...pocBudgets };
    delete newBudgets[pocId];
    setPocBudgets(newBudgets);
    
    // If in OPTIMIZED mode and there are still POCs remaining, re-optimize (force refetch)
    if (allocationMode === BudgetAllocationMode.OPTIMIZED && newSelected.length > 0) {
      // console.log('POC removed in optimized mode - re-optimizing remaining POCs');
      await performBudgetOptimization(newSelected, true); // Force refetch since POC list changed
    }
  };


  const handleBudgetChange = (pocId, budget) => {
    const budgetValue = budget === '' ? null : parseInt(budget);
    setPocBudgets(prev => ({ ...prev, [pocId]: budgetValue }));
    
    const updatedPocs = selectedPocs.map(poc => 
      poc.poc_id === pocId ? { ...poc, poc_budget: budgetValue } : poc
    );
    onPocsChange(updatedPocs);
  };

  const handleApplyToAll = () => {
    if (bulkBudget && !isNaN(bulkBudget)) {
      const budgetValue = parseInt(bulkBudget);
      const newBudgets = {};
      selectedPocs.forEach(poc => {
        newBudgets[poc.poc_id] = budgetValue;
      });
      setPocBudgets(newBudgets);
      
      const updatedPocs = selectedPocs.map(poc => ({ ...poc, poc_budget: budgetValue }));
      onPocsChange(updatedPocs);
    }
  };

  const handleBulkApply = () => {
    if (globalBudgetInput && !isNaN(globalBudgetInput)) {
      const budgetValue = parseInt(globalBudgetInput);
      const newBudgets = {};
      selectedPocs.forEach(poc => {
        newBudgets[poc.poc_id] = budgetValue;
      });
      setPocBudgets(newBudgets);
      
      const updatedPocs = selectedPocs.map(poc => ({ ...poc, poc_budget: budgetValue }));
      onPocsChange(updatedPocs);
    }
  };

  const updateAllocationMode = useCallback((mode) => {
    setAllocationMode(mode);
  }, [setAllocationMode]);

  const handleIndividualBudgetChange = (pocId, budget) => {
    handleBudgetChange(pocId, budget);
  };

  // Handle select all POCs
  const handleSelectAll = (checked) => {
    if (checked) {
      // Add all pocs that aren't already selected
      const newPocs = pocs.filter(poc => !selectedPocs.some(p => p.poc_id === poc.poc_id));
      const pocsToAdd = newPocs.map(poc => ({
        poc_id: poc.poc_id,
        campaign_id: campaignId,
        poc_budget: null,
        poc_name: poc.poc_name,
        address: poc.address,
        city: poc.city,
        region: poc.region,
        country: poc.country,
        latitude: poc.latitude,
        longitude: poc.longitude
      }));
      onPocsChange([...selectedPocs, ...pocsToAdd]);
    } else {
      // Remove all currently visible pocs from selection
      const pocsToRemove = pocs.map(poc => poc.poc_id);
      const newSelected = selectedPocs.filter(p => !pocsToRemove.includes(p.poc_id));
      onPocsChange(newSelected);
      
      // Remove budgets for deselected POCs
      const newBudgets = { ...pocBudgets };
      pocsToRemove.forEach(id => delete newBudgets[id]);
      setPocBudgets(newBudgets);
    }
  };

  // Check if over budget
  const isOverBudget = remainingBudget < 0;

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    try {
      const result = await processPOCFiles(files);
      if (result && result.pocs) {
        onPocsChange([...selectedPocs, ...result.pocs]);
      }
    } catch (error) {
      console.error('File upload error:', error);
    }
  };

  const handleTemplateDownload = () => {
    // Create CSV template
    const headers = ['POC ID', 'POC Name', 'Location', 'Allocated Budget'];
    const csvContent = headers.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'poc_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Expose validate method to parent component
  useImperativeHandle(ref, () => ({
    validate: () => {
      if (selectedPocs.length === 0) {
        alert('Please select at least one POC');
        return false;
      }
      if (isOverBudget) {
        alert('Total allocated budget exceeds campaign budget');
        return false;
      }
      return true;
    },
    getData: () => ({
      selectedPocs,
      pocBudgets,
      allocationMode,
      budgetAllocationModeEnum: allocationMode === BudgetAllocationMode.MANUAL ? BudgetAllocationModeEnum.BPP : BudgetAllocationModeEnum.BO,
      budgetPerPoc: allocationMode === BudgetAllocationMode.MANUAL ? (globalBudgetInput || 0) : -1
    })
  }));
  
  // Budget Optimization handler
  const { getOptimizedBudgets, applyOptimizedBudgetsToPOCs } = useCampaignAPI();
  const [optimizing, setOptimizing] = useState(false);

  // Get campaignData from persistence (local storage)
  const { campaignData } = useCampaignPersistence(region, brand);

  // Helper to check if POCs have optimization data
  const checkHasOptimizationData = useCallback((pocs) => {
    if (!pocs || pocs.length === 0) return false;
    // Check if all POCs have pred_budget and Overlapping fields
    return pocs.every(poc => 
      poc.pred_budget !== undefined && 
      poc.pred_budget !== null && 
      poc.Overlapping !== undefined
    );
  }, []);

  // Helper to build optimization payload
  const buildOptimizationPayload = useCallback((pocsToOptimize) => {
    if (!pocsToOptimize || pocsToOptimize.length === 0) {
      throw new Error('No POCs to optimize');
    }
    
    return {
      pocs: pocsToOptimize.map(({ poc_id, poc_name }) => ({ poc_id, poc_name })),
      region,
      brand: brand || '',
      campaign_id: campaignId,
      start_date: campaignData.startDate || '',
      end_date: campaignData.endDate || '',
    };
  }, [region, brand, campaignId, campaignData]);

  // Main optimization function - reusable
  const performBudgetOptimization = useCallback(async (pocsToOptimize, forceRefetch = false) => {
    if (!pocsToOptimize || pocsToOptimize.length === 0) {
      // console.log('No POCs to optimize');
      return null;
    }
    
    // Check if data already exists and we're not forcing a refetch
    if (!forceRefetch && checkHasOptimizationData(pocsToOptimize)) {
      // console.log('Optimization data already exists, skipping API call');
      return { updatedPocs: pocsToOptimize, budgetMap: pocBudgets };
    }
    
    setOptimizing(true);
    try {
      const payload = buildOptimizationPayload(pocsToOptimize);
      const optimizedList = await getOptimizedBudgets(payload);
      
      // Apply optimized budgets to POCs
      const { updatedPocs, budgetMap } = applyOptimizedBudgetsToPOCs(pocsToOptimize, optimizedList);
      
      // Sort POCs by Overlapping value in ascending order, with '-' values at the end
      const sortedPocs = [...updatedPocs].sort((a, b) => {
        const aOverlapping = a.Overlapping;
        const bOverlapping = b.Overlapping;
        
        // If both are '-', maintain current order
        if (aOverlapping === '-' && bOverlapping === '-') return 0;
        
        // If only a is '-', put it after b
        if (aOverlapping === '-') return 1;
        
        // If only b is '-', put it after a
        if (bOverlapping === '-') return -1;
        
        // Parse as numbers and compare
        const aNum = parseFloat(aOverlapping);
        const bNum = parseFloat(bOverlapping);
        
        // Handle NaN cases
        if (isNaN(aNum) && isNaN(bNum)) return 0;
        if (isNaN(aNum)) return 1;
        if (isNaN(bNum)) return -1;
        
        // Sort in ascending order
        return aNum - bNum;
      });
      
      // Update state with sorted POCs
      setPocBudgets(budgetMap);
      onPocsChange(sortedPocs);
      
      return { updatedPocs: sortedPocs, budgetMap };
    } catch (err) {
      console.error('Budget optimization failed:', err);
      alert('Failed to optimize budgets: ' + (err.message || 'Unknown error'));
      return null;
    } finally {
      setOptimizing(false);
    }
  }, [buildOptimizationPayload, getOptimizedBudgets, applyOptimizedBudgetsToPOCs, setPocBudgets, onPocsChange, checkHasOptimizationData, pocBudgets]);

  // Handle budget optimization button click
  const handleBudgetOptimization = useCallback(async () => {
    if (!selectedPocs.length) return;
    // console.log('Starting budget optimization for POCs:', selectedPocs);
    await performBudgetOptimization(selectedPocs);
  }, [selectedPocs, performBudgetOptimization]);

  return (
    <div className="max-w-7xl mx-auto">
      <Card className="border-0 shadow-sm">
        <CardContent className="p-8">
          {/* Budget Summary Cards */}
          <div className="grid grid-cols-3 gap-6 mb-8">
            {/* Budget Utilization */}
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">BUDGET UTILIZATION</h3>
                </div>
                <div className="flex items-center gap-4">
                  <div className="relative w-20 h-20">
                    <svg className="w-20 h-20 transform -rotate-90">
                      <circle
                        cx="40"
                        cy="40"
                        r="32"
                        stroke="#E5E7EB"
                        strokeWidth="8"
                        fill="transparent"
                      />
                      <circle
                        cx="40"
                        cy="40"
                        r="32"
                        stroke="#3B82F6"
                        strokeWidth="8"
                        fill="transparent"
                        strokeDasharray={`${(allocatedBudget / totalBudget) * 201} 201`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-lg font-semibold text-gray-900">
                        {Math.round((allocatedBudget / totalBudget) * 100)}%
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      ${allocatedBudget.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500">Total Approved</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Remaining Budget */}
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">REMAINING</h3>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-3xl font-bold text-green-600 mb-1">
                  ${remainingBudget.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">
                  out of ${totalBudget.toLocaleString()}
                </div>
              </CardContent>
            </Card>

            {/* Selected POCs */}
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">SELECTED POCS</h3>
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-blue-600 mb-1">
                  {selectedPocs.length}
                </div>
                <div className="text-sm text-gray-500">
                  out of {totalCount || 0} available
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden min-h-[500px] mb-6">
            {/* Tabs Header */}
            <div className="flex border-b border-gray-100 bg-gray-50/50 p-2 gap-2">
             <button
                onClick={() => setActiveTab('search')}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-xl transition-all ${
                  activeTab === 'search' 
                    ? 'bg-white text-blue-600 shadow-sm border border-gray-200' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Search size={18} />
                Search & Add
              </button>
              <button
                onClick={() => setActiveTab('selected')}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-xl transition-all ${
                  activeTab === 'selected' 
                    ? 'bg-white text-blue-600 shadow-sm border border-gray-200' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <List size={18} />
                Selected POCs
                <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] ${
                  activeTab === 'selected' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {selectedPocs.length}
                </span>
              </button>
             
            </div>

            <div className="p-6">
              {/* Tab Content */}
              {activeTab === 'search' ? (
            <>
              {/* Search and Actions */}
              <div className="flex items-center gap-4 mb-6">
                {/* Search POCs */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by ID, Name or Location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* Template Download */}
                <Button
                  onClick={handleTemplateDownload}
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Template
                </Button>

                {/* Upload List */}
                <Button
                  variant="default"
                  className="whitespace-nowrap bg-orange-500 hover:bg-orange-600"
                  onClick={() => document.getElementById('poc-file-upload').click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload List
                </Button>
                <input
                  id="poc-file-upload"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  multiple
                />
              </div>

              {/* Available POCs List */}
              {pocs.length > 0 ? (
                <div className="mb-6">
                  <div className="bg-gray-50 border-b p-4 rounded-t-lg border border-gray-100">
                    <h3 className="font-semibold text-gray-900">Available POCs ({totalCount || 0})</h3>
                    <p className="text-sm text-gray-500">Click on a POC to add it to your selection</p>
                  </div>
                  <POCTable
                    columns={searchPocsColumns(selectedPocs, pocs, handlePocSelect, handleSelectAll)}
                    data={pocs}
                    onRowClick={handlePocSelect}
                    rowClassName={(poc) => 
                      selectedPocs.some(p => p.poc_id === poc.poc_id)
                        ? 'bg-blue-50 cursor-pointer transition-colors'
                        : 'hover:bg-gray-50 cursor-pointer transition-colors'
                    }
                    maxHeight="400px"
                    emptyMessage="No POCs found"
                    onLoadMore={() => {
                      if (searchMode === 'query' && searchTerm.trim().length >= 2) {
                        loadMorePOCs(region, searchTerm.trim());
                      } else if (searchMode === 'region' && region) {
                        loadMorePOCs(region, null);
                      }
                    }}
                    hasMore={hasMorePOCs}
                    loading={pocsLoading}
                  />
                </div>
              ) : (
                <div className="border rounded-lg p-12 text-center text-gray-500">
                  <Search className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">No POCs found</p>
                  <p className="text-sm">
                    {searchTerm.trim() 
                      ? 'Try adjusting your search criteria' 
                      : 'Start searching to find POCs'}
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Selected POCs Tab */}
              <div className="space-y-6 animate-fadeIn">
                {/* Budget Mode Toggle Area */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-blue-50/30 rounded-2xl border border-blue-100">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-blue-600 text-white rounded-lg">
                      <DollarSign size={20} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">Allocation Strategy</h4>
                      <p className="text-xs text-gray-500">How would you like to distribute the budget?</p>
                    </div>
                  </div>
                  
                  <div className="flex p-1 bg-white rounded-xl shadow-sm border border-gray-200 self-start sm:self-center">
                    <button
                      onClick={() => updateAllocationMode(BudgetAllocationMode.MANUAL)}
                      className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                        allocationMode === BudgetAllocationMode.MANUAL 
                          ? 'bg-blue-600 text-white shadow-md' 
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      Budget Per POC
                    </button>
                    <button
                      onClick={async () => {
                        updateAllocationMode(BudgetAllocationMode.OPTIMIZED);
                        // Only fetch optimization data if it doesn't exist
                        if (!checkHasOptimizationData(selectedPocs)) {
                          await handleBudgetOptimization();
                        }
                      }}
                      className={`px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center gap-2 ${
                        allocationMode === BudgetAllocationMode.OPTIMIZED 
                          ? 'bg-blue-600 text-white shadow-md' 
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                      disabled={optimizing || !selectedPocs.length}
                    >
                      <Sparkles size={14} />
                      {optimizing ? 'Optimizing...' : 'Budget Optimization'}
                    </button>
                  </div>
                </div>

                {selectedPocs.length > 0 ? (
                  <>
                    {allocationMode === BudgetAllocationMode.MANUAL && (
                      <div className="flex items-end gap-3 pb-2 animate-fadeIn">
                        <div className="w-44">
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
                            Apply to all ($)
                          </label>
                          <input 
                            type="number" 
                            value={globalBudgetInput}
                            onChange={(e) => setGlobalBudgetInput(e.target.value)}
                            placeholder="100"
                            className="w-full px-3 py-2 border-2 border-gray-100 rounded-lg text-sm focus:border-yellow-600 focus:outline-none"
                          />
                        </div>
                        <button 
                          onClick={handleBulkApply}
                          disabled={!globalBudgetInput || selectedPocs.length === 0}
                          className="px-4 py-2.5 bg-gray-900 text-white text-xs font-bold rounded-lg hover:bg-gray-800 transition-colors shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          Bulk Apply
                        </button>
                      </div>
                    )}

                    {/* Use SelectedPOCsList component */}
                    <SelectedPOCsList
                      pocs={selectedPocs}
                      pocBudgets={pocBudgets}
                      allocationMode={allocationMode}
                      columns={
                        allocationMode === BudgetAllocationMode.MANUAL
                          ? selectedPocsManualColumns(pocBudgets, handleIndividualBudgetChange, handleRemovePOC)
                          : [
                              ...selectedPocsOptimizedColumns(pocBudgets, handleIndividualBudgetChange, handleRemovePOC),
                              {
                                Header: 'Overlapping',
                                accessor: 'Overlapping',
                                Cell: ({ value }) => value || '-',
                              },
                            ]
                      }
                      onBudgetChange={handleIndividualBudgetChange}
                      onRemovePOC={handleRemovePOC}
                      isOverBudget={isOverBudget}
                      remainingBudget={remainingBudget}
                      totalPocs={selectedPocs.length}
                      BudgetAllocationMode={BudgetAllocationMode}
                    />
                  </>
                ) : (
                  <div className="py-20 text-center border-2 border-dashed border-gray-100 rounded-2xl">
                    <Users className="mx-auto text-gray-200 mb-4" size={48} />
                    <h4 className="text-gray-900 font-bold">Your list is empty</h4>
                    <p className="text-gray-500 text-sm mb-6">Start adding locations from the search tab.</p>
                    <button 
                      onClick={() => setActiveTab('search')}
                      className="px-6 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 shadow-md transition-colors"
                    >
                      Go to Search
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default POCSelectionForm;
